if (!customElements.get('bulk-order-grid')) {
  class BulkOrderGrid extends HTMLElement {
    constructor() {
      super();
      this.table = this.querySelector('.bulk-order-grid__table');
      this.theadRow = this.table.querySelector('thead tr');
      this.tbody = this.table.querySelector('tbody');
      this.totalQtyEl = this.querySelector('[data-bulk-total-qty]');
      this.totalPriceEl = this.querySelector('[data-bulk-total-price]');
      this.submitBtn = this.querySelector('[data-bulk-add-all]');
      this.messageEl = this.querySelector('[data-bulk-message]');
      this.siblingsRequested = false;

      this.addEventListener('input', this.handleInput.bind(this));
      this.addEventListener('change', this.sanitizeInput.bind(this));
      this.submitBtn.addEventListener('click', this.handleSubmit.bind(this));

      this.bindQtyInputs();

      const details = this.closest('details');
      if (details && this.dataset.styleCode) {
        details.addEventListener('toggle', () => {
          if (details.open && !this.siblingsRequested) {
            this.siblingsRequested = true;
            this.loadSiblingColours();
          }
        });
      }
    }

    bindQtyInputs() {
      this.qtyInputs = Array.from(this.querySelectorAll('.bulk-order-grid__qty'));
      this.recalculate();
    }

    handleInput(evt) {
      if (!evt.target.classList.contains('bulk-order-grid__qty')) return;
      this.recalculate();
    }

    sanitizeInput(evt) {
      if (!evt.target.classList.contains('bulk-order-grid__qty')) return;
      const input = evt.target;
      const value = Math.max(0, Math.floor(Number(input.value) || 0));
      input.value = value === 0 ? 0 : value;
      this.recalculate();
    }

    recalculate() {
      let totalQty = 0;
      let totalCents = 0;

      this.qtyInputs.forEach((input) => {
        const qty = Math.max(0, Math.floor(Number(input.value) || 0));
        if (qty > 0) {
          totalQty += qty;
          totalCents += qty * Math.round(Number(input.dataset.variantPrice) * 100);
        }
      });

      this.totalQtyEl.textContent = totalQty;
      this.totalPriceEl.textContent = BulkOrderGrid.formatMoney(totalCents, window.theme?.settings?.moneyWithCurrencyFormat);
      this.submitBtn.disabled = totalQty === 0;
    }

    setMessage(text, state) {
      if (!this.messageEl) return;
      this.messageEl.textContent = text;
      this.messageEl.hidden = !text;
      if (state) {
        this.messageEl.dataset.state = state;
      } else {
        delete this.messageEl.dataset.state;
      }
    }

    /**
     * This store lists each colour of a style as its own product, linked only by a
     * shared SKU prefix (e.g. EWD208_BLK_28 / EWD208_404_28). There's no native
     * Shopify link between them, so sibling colours are found via the storefront's
     * own predictive search and merged into one colour x size matrix client-side.
     */
    async loadSiblingColours() {
      const styleCode = this.dataset.styleCode;
      this.setMessage(this.dataset.loadingText, null);

      try {
        const searchUrl = `/search/suggest.json?q=${encodeURIComponent(styleCode)}&resources[type]=product&resources[limit]=10&resources[options][fields]=variants.sku`;
        const searchRes = await fetch(searchUrl, { headers: { Accept: 'application/json' } });
        const searchData = await searchRes.json();
        const candidateHandles = (searchData.resources?.results?.products || []).map((p) => p.handle);

        if (!candidateHandles.includes(this.dataset.currentHandle)) {
          candidateHandles.unshift(this.dataset.currentHandle);
        }

        const handles = Array.from(new Set(candidateHandles));
        const products = await Promise.all(
          handles.map((handle) =>
            fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
              .then((res) => (res.ok ? res.json() : null))
              .catch(() => null)
          )
        );

        const matches = products.filter((product) => {
          const sku = product?.variants?.[0]?.sku;
          return sku && sku.split('_')[0] === styleCode;
        });

        if (matches.length <= 1) {
          this.setMessage('', null);
          return;
        }

        this.renderMatrix(matches);
        this.setMessage('', null);
      } catch (error) {
        this.setMessage('', null);
      }
    }

    renderMatrix(products) {
      const titles = products.map((product) => product.title);
      let prefixLength = titles[0].length;
      for (let i = 1; i < titles.length; i += 1) {
        let shared = 0;
        while (shared < prefixLength && shared < titles[i].length && titles[0][shared] === titles[i][shared]) {
          shared += 1;
        }
        prefixLength = shared;
      }
      const commonPrefix = titles[0].slice(0, prefixLength);

      const sizes = [];
      products.forEach((product) => {
        product.variants.forEach((variant) => {
          if (variant.option1 && !sizes.includes(variant.option1)) sizes.push(variant.option1);
        });
      });

      this.theadRow.innerHTML = '';
      this.theadRow.appendChild(BulkOrderGrid.createHeaderCell(this.dataset.colourLabel || 'Colour', true));
      sizes.forEach((size) => this.theadRow.appendChild(BulkOrderGrid.createHeaderCell(size, false)));

      this.tbody.innerHTML = '';
      products.forEach((product) => {
        const colourName = product.title.slice(commonPrefix.length).trim() || product.title;
        const tr = document.createElement('tr');
        tr.className = 'bulk-order-grid__row';

        const labelTh = document.createElement('th');
        labelTh.scope = 'row';
        labelTh.className = 'bulk-order-grid__row-label';

        const swatch = document.createElement('span');
        swatch.className = 'bulk-order-grid__swatch';
        swatch.setAttribute('aria-hidden', 'true');
        swatch.style.backgroundColor = BulkOrderGrid.colourToCss(colourName);
        labelTh.appendChild(swatch);
        labelTh.appendChild(document.createTextNode(colourName));
        tr.appendChild(labelTh);

        sizes.forEach((size) => {
          const variant = product.variants.find((v) => v.option1 === size);
          tr.appendChild(BulkOrderGrid.createCell(variant, `${colourName} / ${size}`));
        });

        this.tbody.appendChild(tr);
      });

      this.bindQtyInputs();
    }

    /**
     * These colour names come from parsing product titles, not a real Shopify
     * swatch, so common workwear names that aren't valid CSS keywords (stone,
     * charcoal, etc.) need a manual lookup. Anything else falls back to the
     * browser's own colour parsing, then a neutral grey if that fails too.
     */
    static colourToCss(name) {
      const known = {
        stone: '#b7ab98',
        charcoal: '#36454f',
        army: '#4b5320',
        khaki: '#c3b091',
        bone: '#e3dac9',
        sand: '#c2b280',
        slate: '#708090',
        graphite: '#41424c'
      };
      const key = name.trim().toLowerCase();
      if (known[key]) return known[key];
      if (typeof CSS !== 'undefined' && CSS.supports?.('color', name)) return name;
      return '#cccccc';
    }

    static createHeaderCell(text, isRowLabel) {
      const th = document.createElement('th');
      th.scope = 'col';
      if (isRowLabel) th.className = 'bulk-order-grid__row-label';
      th.textContent = text;
      return th;
    }

    static createCell(variant, label) {
      const td = document.createElement('td');
      td.className = 'bulk-order-grid__cell';

      if (!variant) {
        const span = document.createElement('span');
        span.className = 'bulk-order-grid__unavailable';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = '—';
        td.appendChild(span);
        return td;
      }

      const labelEl = document.createElement('label');
      labelEl.className = 'visually-hidden';
      labelEl.setAttribute('for', `BulkQty-${variant.id}`);
      labelEl.textContent = label;

      const input = document.createElement('input');
      input.type = 'number';
      input.inputMode = 'numeric';
      input.className = 'bulk-order-grid__qty';
      input.id = `BulkQty-${variant.id}`;
      input.min = '0';
      input.step = '1';
      input.value = '0';
      if (!variant.available) input.disabled = true;
      input.dataset.variantId = variant.id;
      input.dataset.variantPrice = (variant.price / 100).toString();

      td.appendChild(labelEl);
      td.appendChild(input);
      return td;
    }

    async handleSubmit() {
      const items = this.qtyInputs
        .map((input) => ({ id: Number(input.dataset.variantId), quantity: Math.floor(Number(input.value) || 0) }))
        .filter((item) => item.quantity > 0);

      if (items.length === 0) return;

      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('is-loading');
      this.setMessage('', null);

      try {
        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const data = await response.json();
        if (!response.ok) {
          const error = typeof data.description === 'string' ? data.description : data.message;
          throw new Error(error || this.dataset.errorText);
        }

        this.qtyInputs.forEach((input) => {
          input.value = 0;
        });
        this.recalculate();
        this.setMessage(this.dataset.addedText, 'success');

        this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { items: data } }));
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));

        if (window.theme?.settings?.afterAddToCart === 'drawer') {
          document.querySelector('.js-cart-drawer')?.open(this.submitBtn);
        }
      } catch (error) {
        this.setMessage(error.message || this.dataset.errorText, 'error');
        this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: error.message } }));
      } finally {
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.disabled = false;
        this.recalculate();
      }
    }

    static formatMoney(cents, format) {
      format = format || '${{amount}}';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholderRegex);
      if (!match) return format;

      const formatWithDelimiters = (number, precision, thousands, decimal) => {
        precision = precision == null ? 2 : precision;
        thousands = thousands == null ? ',' : thousands;
        decimal = decimal == null ? '.' : decimal;
        const fixed = (Math.abs(number) / 100).toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        const centsPart = parts[1] ? decimal + parts[1] : '';
        return dollars + centsPart;
      };

      let value;
      switch (match[1]) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return format.replace(placeholderRegex, value);
    }
  }

  customElements.define('bulk-order-grid', BulkOrderGrid);
}
